export const KV_LOADER = 'knowledgevisionLoader';

export enum KV_PARAMS {
  KV_TOKEN = 'kvToken',
  KV_ID = 'kvID',
  KV_NAME = 'kvName'
}

export type TrackingParamsType = Record<KV_PARAMS, string>;
export type KVLoaderType = { loaded: boolean, trackParameters(params: TrackingParamsType): void }

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Wrapper on knowledgevisionLoader
 */
export class KvEmbedTrackingService {
  private static readonly serviceName: string = KV_LOADER;
  private static readonly READY_POLL_MS = 50;
  private static readonly READY_POLL_ATTEMPTS = 40; // ~2s — loader may lag the script DOM load event

  private static getInstance = (): KVLoaderType => {
    if (!Reflect.has(window, KvEmbedTrackingService.serviceName)) {
      throw new Error(`Service ${KvEmbedTrackingService.serviceName} is not available`);
    }

    return Reflect.get(window, KvEmbedTrackingService.serviceName);
  };

  private static isReady = (): boolean => {
    try {
      return KvEmbedTrackingService.getInstance().loaded;
    } catch {
      return false;
    }
  };

  private static pushTrackParameters = (trackingParams: TrackingParamsType): void => {
    const _instance = KvEmbedTrackingService.getInstance();
    if (Reflect.has(_instance, 'trackParameters')) _instance.trackParameters(trackingParams);
  };

  public static pushIfReady = (trackingParams: TrackingParamsType): boolean => {
    if (!KvEmbedTrackingService.isReady()) return false;
    KvEmbedTrackingService.pushTrackParameters(trackingParams);
    return true;
  };

  public static trackData = async (trackingParams: TrackingParamsType): Promise<void> => {
    if (KvEmbedTrackingService.pushIfReady(trackingParams)) return;

    for (let attempt = 0; attempt < KvEmbedTrackingService.READY_POLL_ATTEMPTS; attempt++) {
      await delay(KvEmbedTrackingService.READY_POLL_MS);
      if (KvEmbedTrackingService.pushIfReady(trackingParams)) return;
    }
  };
}
